import { FirebaseError } from 'firebase/app';
import {
  TotpMultiFactorGenerator,
  getMultiFactorResolver,
  signInWithEmailAndPassword,
  type MultiFactorError,
  type MultiFactorResolver,
} from 'firebase/auth';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { trackEvent } from '../analytics';
import { useAuth } from '../auth/AuthProvider';
import { Field } from '../components/Field';
import { shopName } from '../config';
import { auth } from '../firebase';
import { errorMessage } from '../lib/errors';

export function LoginPage() {
  const { state } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/clients';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [resolver, setResolver] = useState<MultiFactorResolver | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (state.status === 'signedIn') return <Navigate to={from} replace />;

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      trackEvent('login');
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof FirebaseError && err.code === 'auth/multi-factor-auth-required') {
        const mfa = getMultiFactorResolver(auth, err as MultiFactorError);
        if (mfa.hints.some((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID)) {
          setResolver(mfa);
        } else {
          setError('This account uses a second factor this app does not support. Ask an admin.');
        }
      } else {
        setError(errorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    if (!resolver) return;
    setError('');
    setBusy(true);
    try {
      const hint = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID)!;
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code.replace(/\s/g, ''));
      await resolver.resolveSignIn(assertion);
      trackEvent('login');
      navigate(from, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-card">
      <h1>{shopName}</h1>
      {!resolver ? (
        <form onSubmit={submitPassword} noValidate>
          <p className="muted">Sign in with your staff account.</p>
          <Field
            label="Email"
            name="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Field
            label="Password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && (
            <p className="alert alert--error" role="alert">
              {error}
            </p>
          )}
          <button className="btn btn--primary btn--block" type="submit" disabled={busy || !email || !password}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          <p className="auth-card__links">
            <Link to="/forgot-password" state={{ email }}>
              Forgot password?
            </Link>
          </p>
        </form>
      ) : (
        <form onSubmit={submitCode} noValidate>
          <p className="muted">Enter the 6-digit code from your authenticator app.</p>
          <Field
            label="Authentication code"
            name="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9 ]*"
            maxLength={7}
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          {error && (
            <p className="alert alert--error" role="alert">
              {error}
            </p>
          )}
          <button className="btn btn--primary btn--block" type="submit" disabled={busy || code.trim().length < 6}>
            {busy ? 'Checking…' : 'Verify'}
          </button>
          <p className="auth-card__links">
            <button
              type="button"
              className="btn btn--link"
              onClick={() => {
                setResolver(null);
                setCode('');
                setError('');
              }}
            >
              Back
            </button>
          </p>
        </form>
      )}
    </div>
  );
}
